// CONTRACT:I8-LOCAL-SETTINGS.1.0
/** Error contracts for the settings store. setSetting NEVER silently accepts a bad write — an
 *  unknown key or a wrong-typed/out-of-range value throws here, so a typo or a malformed value
 *  can never corrupt the persisted shape (fail closed, the previous value stays). */

export type LocalSettingsErrorCode = "UNKNOWN_SETTING_KEY" | "INVALID_SETTING_VALUE";

/** Base class so callers can `instanceof LocalSettingsError` to catch the whole family. */
export class LocalSettingsError extends Error {
  readonly code: LocalSettingsErrorCode;
  constructor(code: LocalSettingsErrorCode, message: string) {
    super(message);
    this.name = "LocalSettingsError";
    this.code = code;
    // Restore prototype chain across the ES5 transpile target.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Thrown when setSetting is called with a key that is not a known leaf path (typed rejection). */
export class ErrUnknownSettingKey extends LocalSettingsError {
  readonly key: string;
  constructor(key: string) {
    super("UNKNOWN_SETTING_KEY", `unknown setting key "${key}"`);
    this.name = "ErrUnknownSettingKey";
    this.key = key;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Thrown when setSetting's value is the wrong type or out of the path's accepted range. */
export class ErrInvalidSettingValue extends LocalSettingsError {
  readonly key: string;
  readonly value: unknown;
  constructor(key: string, value: unknown) {
    super("INVALID_SETTING_VALUE", `invalid value for setting "${key}": ${JSON.stringify(value)}`);
    this.name = "ErrInvalidSettingValue";
    this.key = key;
    this.value = value;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}
